import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { openFile, byteSize, Translate, getSortState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './plans.reducer';

export const Plans = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search)
  );

  const plansList = useAppSelector(state => state.plans.entities);
  const loading = useAppSelector(state => state.plans.loading);
  const totalItems = useAppSelector(state => state.plans.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      })
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  return (
    <div>
      <h2 id="plans-heading" data-cy="PlansHeading">
        <Translate contentKey="fitengineerBackendV2App.plans.home.title">Plans</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="fitengineerBackendV2App.plans.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/plans/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="fitengineerBackendV2App.plans.home.createLabel">Create new Plans</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {plansList && plansList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="fitengineerBackendV2App.plans.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('planTitle')}>
                  <Translate contentKey="fitengineerBackendV2App.plans.planTitle">Plan Title</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('planTitle')} />
                </th>
                <th className="hand" onClick={sort('planPrice')}>
                  <Translate contentKey="fitengineerBackendV2App.plans.planPrice">Plan Price</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('planPrice')} />
                </th>
                <th className="hand" onClick={sort('planDescription')}>
                  <Translate contentKey="fitengineerBackendV2App.plans.planDescription">Plan Description</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('planDescription')} />
                </th>
                <th className="hand" onClick={sort('planLogo')}>
                  <Translate contentKey="fitengineerBackendV2App.plans.planLogo">Plan Logo</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('planLogo')} />
                </th>
                <th className="hand" onClick={sort('planSuitableFor')}>
                  <Translate contentKey="fitengineerBackendV2App.plans.planSuitableFor">Plan Suitable For</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('planSuitableFor')} />
                </th>
                <th>
                  <Translate contentKey="fitengineerBackendV2App.plans.planCategory">Plan Category</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {plansList.map((plans, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/plans/${plans.id}`} color="link" size="sm">
                      {plans.id}
                    </Button>
                  </td>
                  <td>{plans.planTitle}</td>
                  <td>{plans.planPrice}</td>
                  <td>{plans.planDescription}</td>
                  <td>
                    {plans.planLogo ? (
                      <div>
                        {plans.planLogoContentType ? (
                          <a onClick={openFile(plans.planLogoContentType, plans.planLogo)}>
                            <img src={`data:${plans.planLogoContentType};base64,${plans.planLogo}`} style={{ maxHeight: '30px' }} />
                            &nbsp;
                          </a>
                        ) : null}
                        <span>
                          {plans.planLogoContentType}, {byteSize(plans.planLogo)}
                        </span>
                      </div>
                    ) : null}
                  </td>
                  <td>{plans.planSuitableFor}</td>
                  <td>
                    {plans.planCategory ? (
                      <Link to={`/plan-category/${plans.planCategory.id}`}>{plans.planCategory.planCategory}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/plans/${plans.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/plans/${plans.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/plans/${plans.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="fitengineerBackendV2App.plans.home.notFound">No Plans found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={plansList && plansList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default Plans;
